import { z } from 'zod';
import { IsoDateSchema, SlugSchema } from './entities.js';

/**
 * Case Wiki — schéma `case_chronologies` (M068).
 *
 * 4 types de chronologies :
 * - `procedural` : 1 par procédure du dossier. Auto-générée par le Reviewer.
 * - `material` : faits matériels chronologiques. Auto-générée.
 * - `correspondence` : courriels / lettres / mises en demeure. Auto-générée.
 * - `custom` : générée par prompt utilisateur (cf. ChronologyFromPromptGenerator).
 *
 * Spec : docs/architecture/case-wiki/data-model.md §5.
 */

export const ChronologyTypeSchema = z.enum([
  'procedural', 'material', 'correspondence', 'custom',
]);
export type ChronologyType = z.infer<typeof ChronologyTypeSchema>;

/**
 * Filtre structuré dérivé d'un prompt utilisateur (chronologies custom).
 * L'agent (Grok) traduit le prompt naturel en ce JSON, qui est ensuite
 * appliqué SQL sur `case_facts` pour produire la liste de `fact_ids`.
 */
export const ChronologyFilterQuerySchema = z.object({
  side: z.array(z.enum(['ours', 'adverse', 'neutral'])).optional(),
  nature: z.array(z.enum([
    'procedural', 'material', 'numeric', 'corporate', 'allegation',
  ])).optional(),
  event_date_from: IsoDateSchema.optional(),
  event_date_to: IsoDateSchema.optional(),
  party_slugs: z.array(SlugSchema).optional(),
  procedure_id: z.string().uuid().nullable().optional(),
  keywords: z.array(z.string().min(2).max(60)).max(20).optional(),
});
export type ChronologyFilterQuery = z.infer<typeof ChronologyFilterQuerySchema>;

export const ChronologySchema = z.object({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  type: ChronologyTypeSchema,
  procedure_id: z.string().uuid().nullable().optional(),
  name: z.string().min(3).max(200),
  description: z.string().max(2000).nullable().optional(),
  /** Prompt original (custom uniquement). */
  prompt: z.string().min(10).max(2000).nullable().optional(),
  /** Snapshot des fact_ids matchant le filtre au moment de la création. */
  fact_ids: z.array(z.string().uuid()).default([]),
  /** Filter query JSON pour rejouer le prompt (custom + auto_refresh). */
  filter_query: ChronologyFilterQuerySchema.nullable().optional(),
  /** Bloque la suppression (chronologies standards). */
  is_standard: z.boolean().default(false),
  /** Si true : à chaque Reviewer run, le filter_query est rejoué. */
  auto_refresh: z.boolean().default(false),
  created_source: z.enum(['agent', 'user', 'agent_validated']).default('agent'),
  created_by: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Chronology = z.infer<typeof ChronologySchema>;

/** Payload pour POST /api/cases/:caseId/wiki/chronologies/from-prompt. */
export const CreateCustomChronologyPromptRequestSchema = z.object({
  prompt: z.string().min(10).max(2000),
});
export type CreateCustomChronologyPromptRequest = z.infer<
  typeof CreateCustomChronologyPromptRequestSchema
>;

/** Réponse intermédiaire (preview) avant validation par le user. */
export const CustomChronologyPreviewSchema = z.object({
  filter_query: ChronologyFilterQuerySchema,
  fact_ids: z.array(z.string().uuid()),
  /** Échantillon (jusqu'à 5 facts) pour validation visuelle. */
  sample: z.array(z.object({
    slug: SlugSchema,
    title: z.string(),
    event_date: IsoDateSchema.nullable(),
  })),
});
export type CustomChronologyPreview = z.infer<typeof CustomChronologyPreviewSchema>;
